/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import 'antd/dist/reset.css';

// import { GlobalStyle } from 'styles/global-styles';

import { Login } from './pages/Login/Loadable';
import { NotFoundPage } from './pages/NotFoundPage/Loadable';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ThemeProvider } from './components/ui/theme-provider';
import { useGlobalSlice } from './slice';
import { Toaster } from './components/ui/toaster';
import 'react-quill/dist/quill.snow.css';

import { Users } from './pages/Users/Loadable';
import { Admins } from './pages/Admins/Loadable';
import { UserPackagesReports } from './pages/UserPackagesReports/Loadable';
import { FileManager } from './pages/FileManager/Loadable';
import { DownloadPackages } from './pages/DownloadPackages/Loadable';
import { Orders } from './pages/Orders/Loadable';
import { DigitalFiles } from './pages/Orders/digital-files/Loadable';

import { ManagePages } from './pages/ManagePages/Loadable';
import { Teams } from './pages/Teams/Loadable';
import FilesSearch from './pages/Files/search';
import { Blogs } from './pages/Blogs/Loadable';
import { Tags } from './pages/Blogs/Tags';
import { RequestFiles } from './pages/RequestFiles/Loadable';
import { Agents } from './pages/Agents/Loadable';
import { Reviews } from './pages/Reviews/Loadable';
import { Videos } from './pages/Videos/Loadable';
import { Toaster as Sonner } from 'sonner';
import AddBlogs from './pages/Blogs/AddBlogs/add';
import EditBlog from './pages/Blogs/AddBlogs/edit';
import { Media } from './pages/Media/Loadable';
import AttributeFormColor from './pages/Product/Attributes/Type/form/attributeForm-2';
import AttributesTypes from './pages/Product/Attributes/Type';
import { Product } from './pages/Product/Loadable';
import AttributesValues from './pages/Product/Attributes/Values';
import { Inventory } from './pages/Product/Inventory';
import InventoryManagement from './pages/InventoryManagement';
import AddProduct from './pages/Product/AddProduct';
import { ProductCategories } from './pages/Product/Categories/index';
import { ProductTags } from './pages/Product/Tags';

import Chatview from './pages/Users/Chat/view';
import OrderDetails from './pages/OrderDetails';
import ChatAuthForm from './pages/Users/Chat/Pages/chat-auth';
import ChatContent from './pages/Users/Chat/Pages/chat-content';

import { Course } from './pages/Course/Loadable';
import { ProductList } from './pages/Product/List';
import EditProduct from './pages/Product/EditProduct';
import SiteSettings from './pages/Settings/SiteSettings';
import { Appearance } from './pages/Settings/Appearance/Loadable';
import { CourseCategories } from './pages/Course/Categories';
import { CourseTags } from './pages/Course/Tags';
import AddCourse from './pages/Course/Add';
import EditCourse from './pages/Course/Edit';

import { CourseDetails } from './pages/CourseDetails/Loadable';
import CourseContent from './pages/CourseDetails/pages/content';
import CourseReviews from './pages/CourseDetails/pages/review';
import { CourseQuestionAndAnswer } from './pages/CourseDetails/pages/question';
import { ApiManager } from './pages/ApiManager/Loadable';
import { Webhook } from './pages/Webhook/Loadable';

import { DashboardOverview } from './pages/Dashboard/Overview';
import { DashboardAnalytics } from './pages/Dashboard/Analytics';
import { DashboardGAAnalytics } from './pages/Dashboard/GAAnalytics';
import { Transactions } from './pages/Transactions/Loadable';
import { DownloadsHistory } from './pages/DownloadsHistory/Loadable';
import { Categories } from './pages/Blogs/Categories';
import { SubCategories } from './pages/Blogs/SubCategories';
import { MenuManagement } from './pages/MenuManagement/Loadable';
import { BannerManagement } from './pages/BannerManagement/Loadable';
import { ErrorLogs } from './pages/ErrorLogs/Loadable';
import { Notifications } from './pages/Notifications/Loadable';
import Profile from './pages/Profile';
import { useSelector } from 'react-redux';
import { selectSettings } from './slice/selectors';
import { mediaBaseUrl } from 'utils/api/endpoints';
import ForgotPasswordPage from './pages/Login/forgot-password';
import ConfirmEmailChangePage from './pages/Login/confirm-email-change';
import ResetPasswordPage from './pages/Login/reset-password';
import PaymentMethods from './pages/PaymentMethods';
import OfflinePaymentMethods from './pages/OfflinePaymentMethods';
import Currencies from './pages/Currencies';
import EmailConfiguration from './pages/EmailConfiguration';
import { TaxManagement } from './pages/TaxManagement/Loadable';
import {
  CouponDiscounts,
  CouponUsageHistory,
} from './pages/CouponDiscounts/Loadable';
import AddEditCoupon from './pages/CouponDiscounts/pages/AddEditCoupon';
import { ContactUsEnquiry } from './pages/ContactUsEnquiry/Loadable';
import { SupplierManagement } from './pages/SupplierManagement/Loadable';
import { DownloadPackageOrders } from './pages/Orders/DownloadPackageOrders/Loadable';
import { CompletedOrders } from './pages/Orders/CompletedOrders/Loadable';
import { CouponUsageReport } from './pages/Reports/CouponUsageReport/Loadable';
import { DownloadPackagePurchasesReport } from './pages/Reports/DownloadPackagePurchasesReport/Loadable';
import Invoices from './pages/Reports/Invoices/Loadable';
import { Seo } from './pages/SeoSettings/Seo/Loadable';
import { SeoSettings } from './pages/SeoSettings/Loadable';
import { BasicSEOSettings } from './pages/SeoSettings/BasicSEO/Loadable';
import { VerificationSettings } from './pages/SeoSettings/Verification/Loadable';
import { SocialMediaSettings } from './pages/SeoSettings/Social/Loadable';
import { AnalyticsSettings } from './pages/SeoSettings/Analytics/Loadable';
import { SitemapRobotsSettings } from './pages/SeoSettings/SitemapRobots/Loadable';
import TelegramBotConfiguration from './pages/TelegramBotConfiguration/Loadable';

export function App() {
  const { i18n } = useTranslation();
  useGlobalSlice();
  const settings = useSelector(selectSettings);
  const primaryColor = settings?.options?.primary_color
    ? `hsl(${settings.options.primary_color})`
    : undefined;
  const favicon = settings?.options?.favicon
    ? `${mediaBaseUrl}/site/${settings.options.favicon}`
    : '/favicon.ico';

  return (
    <BrowserRouter>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
        primaryColor={primaryColor}
      >
        <Helmet
          titleTemplate="%s - "
          defaultTitle=" "
          htmlAttributes={{ lang: i18n.language }}
        >
          <meta name="description" content="" />
          <link rel="icon" type="image/x-icon" href={favicon} />
        </Helmet>
        <Sonner position="top-center" richColors closeButton />

        <Toaster />
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard/overview" element={<DashboardOverview />} />
            <Route
              path="/dashboard/analytics"
              element={<DashboardAnalytics />}
            />
            <Route
              path="/dashboard/ga-analytics"
              element={<DashboardGAAnalytics />}
            />
            <Route
              path="/confirm-email-change"
              element={<ConfirmEmailChangePage />}
            />
            <Route path="/file-manager" element={<FileManager />} />
            <Route path="/file-manager/:folder_id" element={<FileManager />} />
            <Route path="/users" element={<Users />} />
            <Route path="/admins" element={<Admins />} />
            <Route
              path="/user-packages-reports"
              element={<UserPackagesReports />}
            />

            {/* <Route path="/profile" element={<Profile />} /> */}
            <Route path="/users" element={<Users />} />
            <Route path="/chat" element={<Chatview />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/sales/digital-files" element={<DigitalFiles />} />
            <Route
              path="/sales/download-packages"
              element={<DownloadPackageOrders />}
            />
            <Route
              path="/sales/completed-orders"
              element={<CompletedOrders />}
            />
            <Route path="/api-manager/*" element={<ApiManager />} />
            <Route path="/web-hook" element={<Webhook />} />
            <Route path="/menu-management" element={<MenuManagement />} />
            <Route path="/banner-management" element={<BannerManagement />} />
            <Route path="/error-logs" element={<ErrorLogs />} />
            <Route path="/notifications" element={<Notifications />} />

            <Route path="/transactions" element={<Transactions />} />
            <Route path="/profile" element={<Profile />} />

            {/* <Route path="/orders/:id" element={<OrderDetails />} /> */}
            <Route path="/courses" element={<Course />} />
            <Route path="/courses/add" element={<AddCourse />} />
            <Route path="/courses/edit/:id" element={<EditCourse />} />
            <Route path="/courses/categories" element={<CourseCategories />} />
            <Route path="/courses/tags" element={<CourseTags />} />
            <Route path="/courses/:courseId" element={<CourseDetails />} />
            <Route
              path="/courses/:courseId/reviews"
              element={<CourseReviews />}
            />
            <Route
              path="/courses/:courseId/contents"
              element={<CourseContent />}
            />
            <Route
              path="/courses/:courseId/qna"
              element={<CourseQuestionAndAnswer />}
            />
            <Route
              path="/courses/:courseId/content"
              element={<CourseContent />}
            />

            <Route path="/chatAuthForm" element={<ChatAuthForm />} />
            <Route
              path="/reports/download-history"
              element={<DownloadsHistory />}
            />
            <Route
              path="/reports/coupon-usage"
              element={<CouponUsageReport />}
            />
            <Route
              path="/reports/download-package-purchases"
              element={<DownloadPackagePurchasesReport />}
            />
            <Route
              path="/invoices"
              element={<Invoices />}
            />

            <Route path="/download-packages" element={<DownloadPackages />} />
            <Route path="/manage-pages" element={<ManagePages />} />
            <Route path="/teams" element={<Teams />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/add" element={<AddBlogs />} />
            <Route path="/blogs/edit/:id" element={<EditBlog />} />
            <Route path="/blogs/categories" element={<Categories />} />
            <Route
              path="/blogs/sub-categories/:categoryId"
              element={<SubCategories />}
            />
            <Route path="/blogs/tags" element={<Tags />} />
            <Route path="/payment-methods" element={<PaymentMethods />} />
            <Route
              path="/offline-payment-methods"
              element={<OfflinePaymentMethods />}
            />
            <Route path="/tax-management" element={<TaxManagement />} />
            <Route path="/suppliers/*" element={<SupplierManagement />} />
            <Route path="/coupon-discounts" element={<CouponDiscounts />} />
            <Route path="/coupon-discounts/add" element={<AddEditCoupon />} />
            <Route
              path="/coupon-discounts/edit/:id"
              element={<AddEditCoupon />}
            />
            <Route
              path="/coupon-discounts/usage-history"
              element={<CouponUsageHistory />}
            />
            <Route path="/request-files" element={<RequestFiles />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/media" element={<Media />} />
            <Route path="/agents" element={<Agents />} />
            <Route
              path="/reports/user-packages"
              element={<UserPackagesReports />}
            />
            <Route path="/products/attributes" element={<AttributesTypes />} />

            <Route
              path="/products/categories"
              element={<ProductCategories />}
            />

            <Route path="/products/tags" element={<ProductTags />} />

            <Route
              path="/products/attributes/values/:id"
              element={<AttributesValues />}
            />
            <Route path="/enquiry" element={<ContactUsEnquiry />} />
            <Route path="/seo" element={<Seo />} />
            <Route path="/seo-settings" element={<SeoSettings />} />
            <Route path="/seo-settings/basic" element={<BasicSEOSettings />} />
            <Route
              path="/seo-settings/verification"
              element={<VerificationSettings />}
            />
            <Route
              path="/seo-settings/social"
              element={<SocialMediaSettings />}
            />
            <Route
              path="/seo-settings/analytics"
              element={<AnalyticsSettings />}
            />
            <Route
              path="/seo-settings/sitemap-robots"
              element={<SitemapRobotsSettings />}
            />

            <Route path="/products" element={<ProductList />} />
            <Route path="/products/add" element={<AddProduct />} />
            <Route path="/products/edit" element={<EditProduct />} />
            <Route path="/products/inventory" element={<Inventory />} />
            <Route path="/inventory-management" element={<InventoryManagement />} />
            <Route
              path="/products/categories"
              element={<ProductCategories />}
            />
            <Route path="/products/tags" element={<ProductTags />} />
            <Route path="/products/attributes" element={<AttributesTypes />} />

            <Route path="/settings" element={<SiteSettings />} />
            <Route path="/settings/site-settings" element={<SiteSettings />} />
            <Route path="/settings/appearance" element={<Appearance />} />
            <Route path="/settings/currencies" element={<Currencies />} />
            <Route
              path="/email-configuration"
              element={<EmailConfiguration />}
            />
            <Route
              path="/telegram-bot-configuration"
              element={<TelegramBotConfiguration />}
            />
            <Route path="/videos" element={<Videos />} />
          </Route>

          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        {/* <GlobalStyle /> */}
      </ThemeProvider>
    </BrowserRouter>
  );
}
